import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Music2,
  AtSign,
  Smartphone,
  Monitor,
} from "lucide-react";
import type { ScheduledPostPlatform } from "@shared/schema";
import type {
  PostFormData,
  PreviewDeviceMode,
  SchedulerPlatformWithThreads,
  TemporaryMediaItem,
} from "./types";
import { FacebookPreview } from "./previews/FacebookPreview";
import { InstagramPreview } from "./previews/InstagramPreview";
import { ThreadsPreview } from "./previews/ThreadsPreview";
import { LinkedinPreview } from "./previews/LinkedinPreview";
import { TikTokPreview } from "./previews/TikTokPreview";
import { YoutubePreview } from "./previews/YoutubePreview";

const PLATFORM_TABS: { id: SchedulerPlatformWithThreads; icon: typeof Facebook; color: string }[] =
  [
    { id: "facebook", icon: Facebook, color: "#1877F2" },
    { id: "instagram", icon: Instagram, color: "#E4405F" },
    { id: "threads", icon: AtSign, color: "#000000" },
    { id: "linkedin", icon: Linkedin, color: "#0A66C2" },
    { id: "tiktok", icon: Music2, color: "#000000" },
    { id: "youtube", icon: Youtube, color: "#FF0000" },
  ];

const DEFAULT_DEVICE: Record<SchedulerPlatformWithThreads, PreviewDeviceMode> = {
  facebook: "desktop",
  instagram: "mobile",
  threads: "mobile",
  linkedin: "desktop",
  tiktok: "mobile",
  youtube: "desktop",
};

function isRenderableUrl(url?: string): boolean {
  if (!url) return false;
  return url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:");
}

export function PreviewPanel({
  form,
  selectedPlatforms,
  threadsSelected,
  pageName,
  pageId,
  temporaryMedia,
  onClose,
}: {
  form: PostFormData;
  selectedPlatforms: ScheduledPostPlatform[];
  threadsSelected: boolean;
  pageName?: string;
  pageId?: string;
  temporaryMedia: TemporaryMediaItem[];
  onClose?: () => void;
}) {
  const { t } = useTranslation();

  const visibleTabs = useMemo<SchedulerPlatformWithThreads[]>(() => {
    const tabs: SchedulerPlatformWithThreads[] = [...selectedPlatforms];
    if (threadsSelected && !tabs.includes("threads" as SchedulerPlatformWithThreads)) {
      tabs.splice(2, 0, "threads");
    }
    if (tabs.length === 0) tabs.push(form.platform);
    return tabs;
  }, [selectedPlatforms, threadsSelected, form.platform]);

  const [activeTab, setActiveTab] = useState<SchedulerPlatformWithThreads>(visibleTabs[0]);
  const [device, setDevice] = useState<PreviewDeviceMode>(DEFAULT_DEVICE[visibleTabs[0]]);

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      const next = visibleTabs[0];
      setActiveTab(next);
      setDevice(DEFAULT_DEVICE[next]);
    }
  }, [visibleTabs, activeTab]);

  const previewMediaUrl =
    form.mediaUrls[0] ||
    temporaryMedia.find((item) => isRenderableUrl(item.previewUrl))?.previewUrl;
  const previewMediaType: "image" | "video" | undefined = form.mediaUrls[0]
    ? form.mediaType === "video"
      ? "video"
      : "image"
    : temporaryMedia[0]?.mediaType;

  const isMeta = activeTab === "facebook" || activeTab === "instagram";
  const pageAvatarUrl =
    pageId && isMeta ? `https://graph.facebook.com/${pageId}/picture?type=small` : null;
  const displayName = pageName || t("contentScheduler.preview.yourPage");

  const previewProps = {
    caption: form.caption,
    title: activeTab === "youtube" ? form.presets.youtube.title || form.title : form.title,
    mediaUrl: previewMediaUrl,
    mediaType: previewMediaType,
    pageName: displayName,
    pageAvatarUrl,
    device,
  };

  return (
    <div className="flex flex-col h-full bg-muted">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-1 flex-1">
          {visibleTabs.map((platform) => {
            const tab = PLATFORM_TABS.find((p) => p.id === platform);
            if (!tab) return null;
            const Icon = tab.icon;
            const isActive = platform === activeTab;
            return (
              <button
                key={platform}
                type="button"
                onClick={() => {
                  setActiveTab(platform);
                  setDevice(DEFAULT_DEVICE[platform]);
                }}
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                  isActive ? "bg-white/[0.12]" : "opacity-50 hover:opacity-90"
                }`}
                title={platform}
              >
                <Icon size={14} style={{ color: tab.color === "#000000" ? "#fff" : tab.color }} />
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-0.5 rounded-md bg-white/[0.04] p-0.5">
          <button
            type="button"
            onClick={() => setDevice("mobile")}
            className={`p-1 rounded ${device === "mobile" ? "bg-white/[0.12] text-white" : "text-white/40"}`}
            title={t("contentScheduler.modal.mobileView")}
          >
            <Smartphone size={12} />
          </button>
          <button
            type="button"
            onClick={() => setDevice("desktop")}
            className={`p-1 rounded ${device === "desktop" ? "bg-white/[0.12] text-white" : "text-white/40"}`}
            title={t("contentScheduler.modal.desktopView")}
          >
            <Monitor size={12} />
          </button>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] text-white/45 hover:text-white/80 px-2"
          >
            × {t("contentScheduler.modal.cancel")}
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-6 flex items-start justify-center">
        {activeTab === "facebook" && <FacebookPreview {...previewProps} />}
        {activeTab === "instagram" && <InstagramPreview {...previewProps} />}
        {activeTab === "threads" && <ThreadsPreview {...previewProps} />}
        {activeTab === "linkedin" && <LinkedinPreview {...previewProps} />}
        {activeTab === "tiktok" && <TikTokPreview {...previewProps} />}
        {activeTab === "youtube" && <YoutubePreview {...previewProps} />}
      </div>
      {activeTab === "youtube" && device === "mobile" && (
        <div className="px-6 pb-3 text-[10px] text-white/35 text-center leading-snug">
          {t("contentScheduler.preview.youtubeShortsHint")}
        </div>
      )}
    </div>
  );
}
